import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatINR } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash,
  UploadSimple,
  ArrowUp,
  ArrowDown,
  SpinnerGap,
  Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  "Food & Dining", "Groceries", "Transport", "Shopping", "Bills & Utilities",
  "Rent", "Entertainment", "Health", "Investment", "Salary", "Transfer", "Other",
];

export default function Transactions() {
  const navigate = useNavigate();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    description: "", amount: "", type: "expense", category: "Food & Dining",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/transactions");
      setTxns(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addTxn = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) return;
    await api.post("/transactions", {
      ...form,
      amount: Number(form.amount),
    });
    toast.success("Transaction added");
    setOpen(false);
    setForm({ description: "", amount: "", type: "expense", category: "Food & Dining" });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/transactions/${id}`);
    setTxns((t) => t.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/statement/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      load();
      if (data.report_id) {
        toast.success(`Imported ${data.imported} transactions`, {
          description: "Opening your spending report…",
        });
        navigate(`/app/reports?id=${data.report_id}`);
      } else {
        toast.success(`Imported ${data.imported} transactions`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse statement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Transactions</h1>
          <p className="text-muted-foreground mt-1">Log money in and out, or import a statement.</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.pdf"
            onChange={upload}
            className="hidden"
            data-testid="statement-file-input"
          />
          <Button
            variant="outline"
            data-testid="upload-statement-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-full gap-2"
          >
            {uploading ? <SpinnerGap size={18} className="animate-spin" /> : <UploadSimple size={18} weight="duotone" />}
            {uploading ? "Parsing…" : "Upload statement"}
          </Button>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-txn-btn" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
                <Plus size={18} weight="bold" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Add transaction</DialogTitle>
              </DialogHeader>
              <form onSubmit={addTxn} className="space-y-4 mt-2">
                <div>
                  <Label>Description</Label>
                  <Input data-testid="txn-desc" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1.5 h-11" placeholder="e.g. Zomato dinner" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input type="number" data-testid="txn-amount" value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="mt-1.5 h-11 font-mono" placeholder="500" required />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="txn-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="txn-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" data-testid="txn-submit" className="w-full h-11 rounded-full bg-primary hover:bg-primary/90">
                  Add transaction
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div>
      ) : txns.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <Receipt size={40} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">No transactions yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add one manually or upload a bank statement to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
          <AnimatePresence initial={false}>
            {txns.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ delay: Math.min(i * 0.02, 0.3) }}
                className="flex items-center gap-4 px-4 sm:px-5 py-3.5 hover:bg-secondary/30 transition-colors duration-200 group"
                data-testid="txn-row"
              >
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                  t.type === "income" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"
                }`}>
                  {t.type === "income" ? <ArrowDown size={18} weight="bold" /> : <ArrowUp size={18} weight="bold" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{t.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.category} · {new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </p>
                </div>
                <p className={`font-mono font-semibold ${t.type === "income" ? "text-primary" : "text-foreground"}`}>
                  {t.type === "income" ? "+" : "−"}{formatINR(t.amount)}
                </p>
                <button
                  onClick={() => remove(t.id)}
                  data-testid={`delete-txn-${t.id}`}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all duration-200"
                >
                  <Trash size={18} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
